import React, { useEffect, useState } from 'react';
import { apoliceService, ApoliceData } from '../../services/apoliceService';
import { ApoliceFormModal } from '../apolice/ApoliceFormModal';

interface PropostaCardProps {
  className?: string;
}

const PropostaCard: React.FC<PropostaCardProps> = ({ className = '' }) => {
  const [propostas, setPropostas] = useState<ApoliceData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedApolice, setSelectedApolice] = useState<ApoliceData | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [expanded, setExpanded] = useState(true);
  const itemsPerPage = 4;

  useEffect(() => {
    loadPropostas();
  }, [currentPage]);

  const loadPropostas = async () => {
    try {
      setLoading(true);
      const response = await apoliceService.getPropostas(currentPage, itemsPerPage);
      setPropostas(response.data);
      setTotalPages(response.totalPages || 1);
      setTotal(response.total || 0);
    } catch (error) {
      // Error handled silently
    } finally {
      setLoading(false);
    }
  };

  const handleClienteClick = async (proposta: ApoliceData) => {
    // Busca os dados completos da apólice antes de abrir o modal
    try {
      if (proposta.id) {
        const response = await apoliceService.getApolices({ search: proposta.id }, '/documentos');
        if (response.data && response.data.length > 0) {
          setSelectedApolice(response.data[0]);
        } else {
          setSelectedApolice(proposta);
        }
      } else {
        setSelectedApolice(proposta);
      }
    } catch (error) {
      setSelectedApolice(proposta);
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedApolice(null);
  };

  const handleSave = () => {
    handleCloseModal();
    loadPropostas(); // Recarrega a lista
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-lg p-4 h-[450px] flex flex-col ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            Proposta
          </h2>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
          <span className="ml-2 text-gray-500 text-sm">Carregando...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`bg-white rounded-lg shadow-lg p-4 ${expanded ? 'h-[450px]' : 'h-auto'} flex flex-col ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            Proposta
            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-semibold">
              {total}
            </span>
          </h2>
          <div className="flex gap-2">
            <button 
              onClick={() => setExpanded(!expanded)}
              className="p-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded transition-colors"
              title={expanded ? 'Recolher' : 'Expandir'}
            >
              <svg 
                className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        </div>

        {expanded && (
          <div className="space-y-3 flex-1 overflow-y-auto max-h-[280px]">
          {propostas.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="text-gray-400 text-4xl mb-2">📄</div>
              <p className="text-gray-500 text-sm">Nenhuma proposta encontrada</p>
            </div>
          ) : (
            propostas.map((proposta) => (
              <div 
                key={proposta.id} 
                className="border border-gray-200 rounded-lg p-3 hover:bg-purple-50 hover:border-purple-200 transition-all duration-200 hover:shadow-sm"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {proposta.numeroApolice || `Proposta ${proposta.id}`}
                      </p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        proposta.status?.nome === 'Ativa' || proposta.status?.nome === 'Ativo'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {proposta.status?.nome || 'Pendente'}
                      </span>
                    </div>
                    
                    <div className="text-xs text-gray-600 mb-1">
                      <span className="font-medium">Vigência:</span>
                      <span className="ml-1">
                        {proposta.inicioVigencia
                          ? new Date(proposta.inicioVigencia).toLocaleDateString('pt-BR')
                          : '-'}
                      </span>
                    </div>
                    
                    <div className="text-xs mb-2">
                      <button
                        onClick={() => handleClienteClick(proposta)}
                        className="text-purple-600 hover:text-purple-800 underline cursor-pointer"
                      >
                        {proposta.cliente?.nome || 'N/A'}
                      </button>
                    </div>

                    {expanded && (
                      <div className="mt-2 space-y-1 text-xs text-gray-600">
                        <p>
                          <span className="font-medium">Seguradora:</span> {proposta.seguradora?.nome || 'N/A'}
                        </p>
                        <p>
                          <span className="font-medium">Valor Prêmio:</span> {proposta.valorPremio ? `R$ ${Number(proposta.valorPremio).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` : 'N/A'}
                        </p>
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-2 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full bg-purple-500"></div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        )}

        {/* Paginação */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-200">
            <button
              onClick={handlePreviousPage}
              disabled={currentPage === 1}
              className="px-3 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              ← Anterior
            </button>
            <span className="text-xs text-gray-600 bg-gray-100 px-2 py-1 rounded">
              {currentPage} de {totalPages}
            </span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-xs rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Próxima →
            </button>
          </div>
        )}
      </div>

      {selectedApolice && (
        <ApoliceFormModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onSave={handleSave}
          apolice={selectedApolice}
          isEditing={true}
        />
      )}
    </>
  );
};

export default PropostaCard;
